// src/app/api/adminApi.ts
//
// All administrative API calls.  Import and use these hooks in admin-facing
// screens — never use them in mentee-facing components.
//
// Authentication flow:
//   1. First deployment: POST /api/admin/auth/seed   (one-time, guarded by SEED_SECRET)
//   2. Subsequent admins: POST /api/admin/auth/invite (ADMIN only)
//   3. All admin logins:  POST /api/admin/auth/login
//   4. The returned token is stored the same way as the mentee token —
//      via setCredentials in authSlice — but the role field distinguishes
//      admin users in the UI routing layer.

import { baseApi } from "./baseApi";
import type { QuarterKey, QuarterStatus } from "./programmeApi";
import type { MemberTier, UserRole } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

// ── Auth ─────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  region?: string | null;
  managedRegion?: string | null;
  ministryRole?: string | null;
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
}

export interface AdminAuthResponse {
  success: boolean;
  message: string;
  data: AdminUser;
  token: string;
}

export interface SeedAdminRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  seedSecret: string;
}

export interface InviteAdminRequest {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  managedRegion?: string;
}

// ── User management ───────────────────────────────────────────────

export interface ManagedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  tier: MemberTier;
  region?: string | null;
  province?: string | null;
  ministryRole?: string | null;
  managedRegion?: string | null;
  isActive: boolean;
  isEmailVerified: boolean;
  fellowsEligible: boolean;
  fellowsEligibleQuarter?: QuarterKey | null;
  programmeStartQuarter?: QuarterKey | null;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  profile?: Record<string, unknown> | null; // MenteeProfile when fetched by id
}

export interface UserListFilters {
  role?: UserRole;
  region?: string;
  tier?: MemberTier;
  search?: string;
  isActive?: boolean;
  limit?: number;
  page?: number;
}

interface UserListResponse {
  success: boolean;
  message: string;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: ManagedUser[];
}

interface UserResponse {
  success: boolean;
  message: string;
  data: ManagedUser;
}

export interface UpdateUserRequest {
  role?: UserRole;
  region?: string;
  province?: string;
  managedRegion?: string;
  ministryRole?: string;
  tier?: MemberTier;
  isActive?: boolean;
}

// ── Programme cycles ──────────────────────────────────────────────

export interface CreateCycleRequest {
  year: number;
  quarter: QuarterKey;
  theme: string;
  tagline?: string;
  status?: QuarterStatus;
  pillars?: string[];
  eventDate: string;
  taskSubmissionDeadline?: string;
  fellowsSelectionDeadline?: string;
  needsAssessmentStart?: string;
  needsAssessmentEnd?: string;
  contentDevStart?: string;
  contentDevEnd?: string;
  mobilisationStart?: string;
  mobilisationEnd?: string;
  shadowingWeekStart?: string;
  shadowingWeekEnd?: string;
}

export interface SeedDefaultCyclesRequest {
  year: number;
  eventDates?: Partial<Record<QuarterKey, string>>;
}

interface CycleResponse {
  success: boolean;
  message: string;
  data: unknown;
}

interface SeedCyclesResponse {
  success: boolean;
  message: string;
  data: {
    quarter: QuarterKey;
    status: "created" | "skipped";
    id?: string;
    reason?: string;
  }[];
}

// ── Tasks ─────────────────────────────────────────────────────────

export interface TaskInstruction {
  stepNumber: number;
  instruction: string;
}

export interface CreateTaskRequest {
  quarter: QuarterKey;
  cycle?: string;
  taskNumber: number;
  title: string;
  description?: string;
  instructions?: TaskInstruction[];
  minWords?: number;
  tags?: string[];
  availableFrom?: string;
  dueDate?: string;
  isPublished?: boolean;
}

export interface BulkCreateTasksRequest {
  quarter: QuarterKey;
  cycle?: string;
  tasks: Omit<CreateTaskRequest, "quarter" | "cycle">[];
}

interface TaskResponse {
  success: boolean;
  message: string;
  data: unknown;
}
interface BulkTaskResponse {
  success: boolean;
  message: string;
  data: {
    created: unknown[];
    skipped: { reason: string; taskNumber?: number }[];
  };
}

// ── Library ───────────────────────────────────────────────────────

export type AdminContentType =
  | "keynote"
  | "panel"
  | "reading"
  | "resource"
  | "task";

export interface CreateLibraryRequest {
  contentType: AdminContentType;
  quarter: QuarterKey;
  cycle?: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  tags?: string[];
  videoUrl?: string;
  thumbnailUrl?: string;
  fileUrl?: string;
  fileName?: string;
  fileSizeBytes?: number;
  fileType?: string;
  readingItems?: {
    title: string;
    author?: string;
    type?: string;
    description?: string;
    externalUrl?: string;
  }[];
  chapterMarkers?: { timeSeconds: number; label: string }[];
  isDownloadable?: boolean;
  isPublished?: boolean;
}

interface LibraryResponse {
  success: boolean;
  message: string;
  data: unknown;
}

// ── Mentee progression ────────────────────────────────────────────

interface AdvanceQuarterResponse {
  success: boolean;
  message: string;
  data: {
    previousQuarter: QuarterKey;
    currentQuarter: QuarterKey;
    quartersCompleted: number;
  };
}

// ── Announcements ─────────────────────────────────────────────────

export interface CreateAnnouncementRequest {
  category: "event" | "deadline" | "upload" | "fellows" | "general";
  title: string;
  body: string;
  quarter?: QuarterKey;
  cycle?: string;
  isPinned?: boolean;
  isPublished?: boolean;
  authorDisplayName?: string;
}

interface AnnouncementResponse {
  success: boolean;
  message: string;
  data: unknown;
}

// ── Dashboard stats ───────────────────────────────────────────────

export interface AdminDashboardStats {
  users: {
    totalMentees: number;
    activeMentees: number;
    totalAdmins: number;
    tierDistribution: {
      wider_audience: number;
      inner_circle: number;
      legacy_fellows: number;
    };
  };
  programme: {
    activeCycle: {
      quarter: QuarterKey;
      theme: string;
      eventDate: string;
    } | null;
  };
  tasks: { total: number; published: number; draft: number };
  library: { total: number; published: number; draft: number };
  fellows: { total: number; selected: number };
  announcements: { published: number };
}

interface StatsResponse {
  success: boolean;
  data: AdminDashboardStats;
}
interface MenteeStatsResponse {
  success: boolean;
  data: Record<string, unknown>;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: null;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ══ AUTHENTICATION ══════════════════════════════════════════════

    // One-time seed — creates the very first super admin.
    // Requires SEED_SECRET env variable to match. Fails if any admin exists.
    seedAdmin: builder.mutation<AdminAuthResponse, SeedAdminRequest>({
      query: (body) => ({ url: "/admin/auth/seed", method: "POST", body }),
    }),

    // Login for any admin-role user (rejects mentees).
    // Store the returned token via setCredentials in authSlice.
    adminLogin: builder.mutation<
      AdminAuthResponse,
      { email: string; password: string }
    >({
      query: (body) => ({ url: "/admin/auth/login", method: "POST", body }),
    }),

    // Invite a new committee/rep/manager account. ADMIN only.
    inviteAdmin: builder.mutation<
      { success: boolean; data: AdminUser },
      InviteAdminRequest
    >({
      query: (body) => ({ url: "/admin/auth/invite", method: "POST", body }),
      invalidatesTags: [{ type: "User", id: "ADMIN_LIST" }],
    }),

    // Get the currently authenticated admin's own profile.
    getAdminMe: builder.query<{ success: boolean; data: AdminUser }, void>({
      query: () => "/admin/auth/me",
      providesTags: [{ type: "User", id: "ADMIN_ME" }],
    }),

    // ══ USER MANAGEMENT ═════════════════════════════════════════════

    // List all users with filters.
    getUsers: builder.query<UserListResponse, UserListFilters>({
      query: (params) => ({ url: "/admin/users", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "User" as const, id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),

    // Get a single user's profile + MenteeProfile.
    getUser: builder.query<UserResponse, string>({
      query: (id) => `/admin/users/${id}`,
      providesTags: (_r, _e, id) => [{ type: "User", id }],
    }),

    // Update role, tier, region, active status.
    updateUser: builder.mutation<
      UserResponse,
      { id: string } & UpdateUserRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/users/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
        { type: "JourneyProfile", id: "CURRENT" },
      ],
    }),

    // Deactivate (soft-delete) a user.
    deactivateUser: builder.mutation<MessageResponse, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    // ══ PROGRAMME CYCLES ════════════════════════════════════════════

    // Create a single programme cycle.
    adminCreateCycle: builder.mutation<CycleResponse, CreateCycleRequest>({
      query: (body) => ({
        url: "/admin/programme/cycle",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ProgrammeCycle", id: "LIST" }],
    }),

    // Seed all four default Q1–Q4 cycles for a given year using the
    // built-in QUARTER_THEMES constants. Skips any that already exist.
    seedDefaultCycles: builder.mutation<
      SeedCyclesResponse,
      SeedDefaultCyclesRequest
    >({
      query: (body) => ({
        url: "/admin/programme/cycle/seed-defaults",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ProgrammeCycle", id: "LIST" },
        { type: "ProgrammeCycle", id: "MY_QUARTER" },
      ],
    }),

    // ══ TASKS ═══════════════════════════════════════════════════════

    // Create a single task definition.
    adminCreateTask: builder.mutation<TaskResponse, CreateTaskRequest>({
      query: (body) => ({ url: "/admin/tasks", method: "POST", body }),
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    // Create multiple task definitions in one request.
    adminBulkCreateTasks: builder.mutation<
      BulkTaskResponse,
      BulkCreateTasksRequest
    >({
      query: (body) => ({ url: "/admin/tasks/bulk", method: "POST", body }),
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    // Update a task definition (edit, publish, set dates).
    adminUpdateTask: builder.mutation<
      TaskResponse,
      { id: string } & Partial<CreateTaskRequest & { isActive: boolean }>
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/tasks/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Task", id },
        { type: "Task", id: "LIST" },
      ],
    }),

    // ══ LIBRARY ═════════════════════════════════════════════════════

    // Upload a new library content item.
    adminCreateLibraryContent: builder.mutation<
      LibraryResponse,
      CreateLibraryRequest
    >({
      query: (body) => ({ url: "/admin/library", method: "POST", body }),
      invalidatesTags: [{ type: "LibraryContent", id: "LIST" }],
    }),

    // Update an existing library content item (edit, publish, mark not-new).
    adminUpdateLibraryContent: builder.mutation<
      LibraryResponse,
      { id: string } & Partial<CreateLibraryRequest & { isNew: boolean }>
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/library/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "LibraryContent", id },
        { type: "LibraryContent", id: "LIST" },
      ],
    }),

    // Permanently delete a library content item.
    adminDeleteLibraryContent: builder.mutation<MessageResponse, string>({
      query: (id) => ({ url: `/admin/library/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "LibraryContent", id: "LIST" }],
    }),

    // ══ MENTEE PROGRESSION ══════════════════════════════════════════

    // Advance a specific mentee to their next quarter (admin override,
    // skips the task-graded check).
    adminAdvanceMenteeQuarter: builder.mutation<
      AdvanceQuarterResponse,
      { menteeId: string; reason?: string }
    >({
      query: ({ menteeId, ...body }) => ({
        url: `/admin/mentees/${menteeId}/advance-quarter`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { menteeId }) => [
        { type: "User", id: menteeId },
        { type: "JourneyProfile", id: "CURRENT" },
        { type: "JourneyQuarters", id: "ALL" },
        { type: "ProgrammeCycle", id: "MY_QUARTER" },
      ],
    }),

    // Override a mentee's tier directly.
    adminSetMenteeTier: builder.mutation<
      UserResponse,
      { menteeId: string; tier: MemberTier }
    >({
      query: ({ menteeId, tier }) => ({
        url: `/admin/mentees/${menteeId}/set-tier`,
        method: "PATCH",
        body: { tier },
      }),
      invalidatesTags: (_r, _e, { menteeId }) => [
        { type: "User", id: menteeId },
        { type: "User", id: "LIST" },
      ],
    }),

    // ══ ANNOUNCEMENTS ═══════════════════════════════════════════════

    // Create and optionally publish an announcement.
    adminCreateAnnouncement: builder.mutation<
      AnnouncementResponse,
      CreateAnnouncementRequest
    >({
      query: (body) => ({ url: "/admin/announcements", method: "POST", body }),
      invalidatesTags: [{ type: "Announcement", id: "LIST" }],
    }),

    // ══ DASHBOARD STATS ═════════════════════════════════════════════

    // High-level aggregate counts for the admin overview page.
    getAdminStats: builder.query<StatsResponse, void>({
      query: () => "/admin/stats",
      providesTags: [{ type: "User", id: "STATS" }],
    }),

    // Per-quarter submission and engagement breakdown for M&E reporting.
    getAdminMenteeStats: builder.query<
      MenteeStatsResponse,
      { quarter?: QuarterKey }
    >({
      query: (params) => ({ url: "/admin/stats/mentees", params }),
    }),
  }),
});

export const {
  // Auth
  useSeedAdminMutation,
  useAdminLoginMutation,
  useInviteAdminMutation,
  useGetAdminMeQuery,
  // User management
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useDeactivateUserMutation,
  // Programme
  useAdminCreateCycleMutation,
  useSeedDefaultCyclesMutation,
  // Tasks
  useAdminCreateTaskMutation,
  useAdminBulkCreateTasksMutation,
  useAdminUpdateTaskMutation,
  // Library
  useAdminCreateLibraryContentMutation,
  useAdminUpdateLibraryContentMutation,
  useAdminDeleteLibraryContentMutation,
  // Mentee progression
  useAdminAdvanceMenteeQuarterMutation,
  useAdminSetMenteeTierMutation,
  // Announcements
  useAdminCreateAnnouncementMutation,
  // Stats
  useGetAdminStatsQuery,
  useGetAdminMenteeStatsQuery,
} = adminApi;
